import { useState, useEffect, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import {
  Lock, LogOut, RefreshCw, Download, Loader2, Users, Inbox, ShieldCheck,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = 'sss_admin_token';

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

/* ─── LOGIN ─── */
const AdminLogin = ({ onLogin }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!username || !password) {
      toast.error('Enter your username and password.');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Login failed');
      localStorage.setItem(TOKEN_KEY, data.token);
      onLogin(data.token);
      toast.success('Welcome back!');
    } catch (err) {
      toast.error(err.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4 py-16">
      <Card className="w-full max-w-md rounded-2xl border-[hsl(var(--border))] shadow-[var(--shadow-lg)]">
        <CardContent className="p-8 sm:p-10">
          <div className="mx-auto h-14 w-14 rounded-2xl bg-[hsl(var(--sss-navy))] flex items-center justify-center">
            <Lock className="h-6 w-6 text-white" />
          </div>
          <h1 className="mt-6 text-2xl font-semibold text-center text-[hsl(var(--sss-navy))]">Admin Login</h1>
          <p className="mt-2 text-sm text-center text-muted-foreground">
            Sign in to view waitlist submissions.
          </p>
          <form onSubmit={handleSubmit} className="mt-8 space-y-5">
            <div>
              <Label htmlFor="admin-username" className="text-[hsl(var(--sss-navy))]">Username</Label>
              <Input
                id="admin-username" data-testid="admin-username-input"
                value={username} onChange={(e) => setUsername(e.target.value)}
                autoComplete="username" className="mt-1.5 h-11 rounded-xl"
              />
            </div>
            <div>
              <Label htmlFor="admin-password" className="text-[hsl(var(--sss-navy))]">Password</Label>
              <Input
                id="admin-password" data-testid="admin-password-input" type="password"
                value={password} onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password" className="mt-1.5 h-11 rounded-xl"
              />
            </div>
            <Button
              type="submit" disabled={loading} data-testid="admin-login-button"
              className="w-full bg-[hsl(var(--sss-navy))] hover:bg-[hsl(var(--sss-navy-2))] text-white rounded-xl h-11 font-medium"
            >
              {loading ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Signing in…</>) : 'Sign In'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

/* ─── DASHBOARD ─── */
const AdminDashboard = ({ token, onLogout }) => {
  const [submissions, setSubmissions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  const fetchSubmissions = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/waitlist`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 401) {
        toast.error('Session expired. Please log in again.');
        onLogout();
        return;
      }
      const data = await res.json();
      setSubmissions(data.submissions || []);
    } catch {
      toast.error('Failed to load submissions.');
      setSubmissions([]);
    } finally {
      setLoading(false);
    }
  }, [token, onLogout]);

  useEffect(() => { fetchSubmissions(); }, [fetchSubmissions]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/waitlist/export`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Export failed');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'waitlist_submissions.csv';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('CSV downloaded.');
    } catch {
      toast.error('Could not export CSV.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="mx-auto max-w-[var(--grid-max)] px-4 sm:px-6 lg:px-8 py-10 sm:py-14" data-testid="admin-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-[hsl(var(--sss-teal))]" />
            <h1 className="text-2xl sm:text-3xl font-semibold text-[hsl(var(--sss-navy))]">Waitlist Submissions</h1>
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            ORCAA First Look — Early Access Waiting List
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={fetchSubmissions} disabled={loading} data-testid="admin-refresh-button" className="rounded-xl border-[hsl(var(--border))]">
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
          <Button onClick={handleExport} disabled={exporting || !submissions?.length} data-testid="admin-export-button" className="rounded-xl bg-[hsl(var(--sss-coral))] hover:bg-[hsl(var(--sss-coral-2))] text-white">
            {exporting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />} Export CSV
          </Button>
          <Button variant="ghost" onClick={onLogout} data-testid="admin-logout-button" className="rounded-xl text-muted-foreground hover:text-[hsl(var(--sss-navy))]">
            <LogOut className="h-4 w-4 mr-2" /> Logout
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="mt-6 inline-flex items-center gap-3 rounded-xl bg-white border border-[hsl(var(--border))] px-5 py-3 shadow-[var(--shadow-sm)]">
        <Users className="h-5 w-5 text-[hsl(var(--sss-teal))]" />
        <span className="text-sm text-muted-foreground">Total signups:</span>
        <span className="text-lg font-bold text-[hsl(var(--sss-navy))]" data-testid="admin-total-count">
          {submissions?.length ?? '—'}
        </span>
      </div>

      {/* Table */}
      <Card className="mt-6 rounded-2xl border-[hsl(var(--border))] shadow-[var(--shadow-sm)] overflow-hidden">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-3">
              {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-12 w-full rounded-lg" />)}
            </div>
          ) : !submissions?.length ? (
            <div className="p-14 text-center" data-testid="admin-empty-state">
              <Inbox className="h-10 w-10 text-muted-foreground/40 mx-auto" />
              <p className="mt-4 text-base font-medium text-[hsl(var(--sss-navy))]">No submissions yet</p>
              <p className="mt-1 text-sm text-muted-foreground">New waitlist signups will appear here.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table data-testid="admin-submissions-table">
                <TableHeader>
                  <TableRow className="bg-[hsl(var(--secondary))]">
                    <TableHead className="whitespace-nowrap">Date</TableHead>
                    <TableHead className="whitespace-nowrap">Name</TableHead>
                    <TableHead className="whitespace-nowrap">Practice</TableHead>
                    <TableHead className="whitespace-nowrap">Email</TableHead>
                    <TableHead className="whitespace-nowrap">Cellphone</TableHead>
                    <TableHead className="whitespace-nowrap">Role</TableHead>
                    <TableHead className="whitespace-nowrap">Full-Arch Vol.</TableHead>
                    <TableHead className="whitespace-nowrap">Leads/mo</TableHead>
                    <TableHead className="whitespace-nowrap">Message</TableHead>
                    <TableHead className="whitespace-nowrap">Email Alert</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {submissions.map((s) => (
                    <TableRow key={s.id} data-testid={`admin-row-${s.id}`}>
                      <TableCell className="whitespace-nowrap text-xs text-muted-foreground">{formatDate(s.created_at)}</TableCell>
                      <TableCell className="whitespace-nowrap font-medium text-[hsl(var(--sss-navy))]">{s.first_name} {s.last_name}</TableCell>
                      <TableCell className="whitespace-nowrap">{s.practice_name}</TableCell>
                      <TableCell className="whitespace-nowrap"><a href={`mailto:${s.email}`} className="text-[hsl(var(--sss-teal))] hover:underline">{s.email}</a></TableCell>
                      <TableCell className="whitespace-nowrap">{s.cellphone}</TableCell>
                      <TableCell className="whitespace-nowrap">{s.role}</TableCell>
                      <TableCell className="whitespace-nowrap">{s.monthly_full_arch_volume}</TableCell>
                      <TableCell className="whitespace-nowrap">{s.new_leads_per_month || '—'}</TableCell>
                      <TableCell className="max-w-[220px] truncate text-xs text-muted-foreground" title={s.message}>{s.message || '—'}</TableCell>
                      <TableCell>
                        <Badge className={`rounded-full text-[10px] font-semibold ${
                          s.email_status === 'sent'
                            ? 'bg-[hsl(var(--accent))] text-[hsl(var(--sss-teal-2))] hover:bg-[hsl(var(--accent))]'
                            : 'bg-[hsl(var(--secondary))] text-muted-foreground hover:bg-[hsl(var(--secondary))]'
                        }`}>
                          {s.email_status === 'sent' ? 'Sent' : 'Failed'}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

/* ─── PAGE ─── */
export default function AdminPage() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));

  const handleLogout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
  }, []);

  return (
    <div data-testid="admin-page" className="bg-[hsl(var(--background))] min-h-[70vh]">
      {token ? (
        <AdminDashboard token={token} onLogout={handleLogout} />
      ) : (
        <AdminLogin onLogin={setToken} />
      )}
    </div>
  );
}
